import { useCallback } from 'react';
import { undoAddedParametersToSpecificFamilies, undoAddedProjectParameters } from '../../../api/revit/revitBridge';
import {
  hasConnectorUndoWork,
  markUndoPlanUnusable,
  UnusableUndoPlanMessage,
} from '../../parameterUndo/undoPlanExecution';
import { invalidateFamilySnapshotCache } from '../../revit/familySnapshotCache';
import { forgetAvailableProjectParameters } from './availableProjectParameterCache';
import { projectChangedResult } from './operationIdentity';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ConnectorParameterUndoPlan } from '../../../types/parameterSetupUndo';

/** The wording that separates one undo action from another; the steps are shared. */
interface UndoActionCopy {
  missingPlanWarning: string;
  foreignProjectWarning: string;
  progressTitle: string;
  progressMessage: string;
  familyFailureFallback: string;
  successMessage: string;
}

interface UndoActionRequest {
  plan: ConnectorParameterUndoPlan | null;
  projectUndoKey: string | null;
  documentIdentity: ParameterAdditionCore['expectedDocumentIdentity'];
  onProgress: ParameterAdditionCore['updateParameterProgressFromHost'];
  didProjectChange: ParameterAdditionCore['didProjectChange'];
  savePlan: (next: ConnectorParameterUndoPlan | null) => void;
  setIsAdding: ParameterAdditionCore['setIsAdding'];
  startProgress: ParameterAdditionCore['startParameterProgress'];
  notify: Pick<ParameterAdditionCore, 'showError' | 'showInfo' | 'showSuccess' | 'showWarning'>;
  copy: UndoActionCopy;
}

const ProgressDetail =
  'Project bindings and family parameters are restored where an undo plan is available.';

const ConnectorCopy: UndoActionCopy = {
  missingPlanWarning: 'No undoable connector parameter setup is available.',
  foreignProjectWarning: 'The saved connector undo plan belongs to another Revit project.',
  progressTitle: 'Reverting connector parameters',
  progressMessage: 'Revit is undoing connector parameter setup',
  familyFailureFallback: 'Failed to undo connector family-parameter changes.',
  successMessage: 'Last connector family-parameter addition was undone.',
};

const AdditionalCategoryCopy: UndoActionCopy = {
  missingPlanWarning: 'No undoable additional-category parameter setup is available.',
  foreignProjectWarning:
    'The saved additional-category undo plan belongs to another Revit project.',
  progressTitle: 'Reverting category parameters',
  progressMessage: 'Revit is undoing category parameter setup',
  familyFailureFallback: 'Failed to undo additional-category family-parameter changes.',
  successMessage: 'Last additional-category parameter setup was undone.',
};

const undoFailed = (
  plan: ConnectorParameterUndoPlan,
  error: string,
): ConnectorParameterUndoPlan => ({ ...plan, status: 'undo-failed', error });

/**
 * Sends the recorded parameters back to Revit and reports the plan as it must
 * be stored afterwards. An `error` means nothing beyond that call succeeded.
 */
const revertRecordedParameters = async (
  request: UndoActionRequest,
  plan: ConnectorParameterUndoPlan,
  projectUndoKey: string,
): Promise<{ plan: ConnectorParameterUndoPlan; error?: string }> => {
  const { documentIdentity, onProgress, copy } = request;

  if (plan.projectParameterNames.length > 0) {
    const projectResult = await undoAddedProjectParameters({
      parameterNames: Array.from(new Set(plan.projectParameterNames)),
      entries: plan.projectEntries,
      ...documentIdentity,
      onProgress,
    });

    if (!projectResult.success) {
      const error = projectResult.error || 'Failed to undo added project parameters.';
      return { plan: undoFailed(plan, error), error };
    }
  }

  if (plan.entries.length > 0) {
    const result = await undoAddedParametersToSpecificFamilies({
      entries: plan.entries.map((entry) => ({
        familyId: entry.familyId,
        familyName: entry.familyName,
        parameterName: entry.parameterName,
        existedBefore: entry.existedBefore,
        previousBindingKind: entry.previousBindingKind,
      })),
      ...documentIdentity,
      onProgress,
    });
    invalidateFamilySnapshotCache(projectUndoKey);

    if (!result.success) {
      const error = result.error || copy.familyFailureFallback;
      return { plan: undoFailed(plan, error), error };
    }
  }

  forgetAvailableProjectParameters(plan.projectParameterNames, projectUndoKey);
  return { plan: { ...plan, status: 'undone', error: undefined } };
};

/**
 * Runs one recorded parameter setup back out of the model. Both undo actions
 * take the same steps and differ only in their wording and their stored plan.
 */
const runUndoAction = async (request: UndoActionRequest): Promise<boolean> => {
  const { plan, projectUndoKey, savePlan, setIsAdding, notify, copy } = request;

  if (!plan || plan.status !== 'available') {
    notify.showWarning(copy.missingPlanWarning);
    return false;
  }

  if (!projectUndoKey || plan.projectKey !== projectUndoKey) {
    savePlan(null);
    notify.showWarning(copy.foreignProjectWarning);
    return false;
  }

  // A record that claims changes but carries none can revert nothing, so it
  // must never run to a success message. It is quarantined instead: the user
  // keeps the note that parameters are applied, and the dead offer disappears.
  if (!hasConnectorUndoWork(plan)) {
    savePlan(markUndoPlanUnusable(plan, UnusableUndoPlanMessage));
    notify.showError(UnusableUndoPlanMessage);
    return false;
  }

  setIsAdding(true);
  request.startProgress({
    title: copy.progressTitle,
    message: copy.progressMessage,
    detail: ProgressDetail,
  });

  try {
    const outcome = await revertRecordedParameters(request, plan, projectUndoKey);
    if (outcome.error) {
      savePlan(outcome.plan);
      notify.showError(outcome.error);
      return false;
    }

    notify.showInfo('Undo request completed. Flow cache updated for this project.');
    if (request.didProjectChange(projectUndoKey)) {
      notify.showWarning(projectChangedResult().error ?? 'The active Revit project changed.');
      return false;
    }

    savePlan(outcome.plan);
    notify.showSuccess(copy.successMessage);
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Undo failed';
    savePlan(undoFailed(plan, message));
    notify.showError(message);
    return false;
  } finally {
    setIsAdding(false);
  }
};

export function useParameterUndoActions(core: ParameterAdditionCore) {
  const {
    currentProjectUndoKey,
    didProjectChange,
    expectedDocumentIdentity,
    lastAdditionalCategoryUndoPlan,
    lastConnectorUndoPlan,
    setAdditionalCategoryParameterUndoPlan,
    setConnectorParameterUndoPlan,
    setIsAdding,
    showError,
    showInfo,
    showSuccess,
    showWarning,
    startParameterProgress,
    updateParameterProgressFromHost,
  } = core;

  const undoLastConnectorParameterAddition = useCallback(
    async (): Promise<boolean> =>
      runUndoAction({
        plan: lastConnectorUndoPlan,
        projectUndoKey: currentProjectUndoKey,
        documentIdentity: expectedDocumentIdentity,
        onProgress: updateParameterProgressFromHost,
        didProjectChange,
        savePlan: setConnectorParameterUndoPlan,
        setIsAdding,
        startProgress: startParameterProgress,
        notify: { showError, showInfo, showSuccess, showWarning },
        copy: ConnectorCopy,
      }),
    [
      currentProjectUndoKey,
      didProjectChange,
      expectedDocumentIdentity,
      lastConnectorUndoPlan,
      setIsAdding,
      setConnectorParameterUndoPlan,
      showError,
      showInfo,
      showSuccess,
      showWarning,
      startParameterProgress,
      updateParameterProgressFromHost,
    ],
  );

  const undoLastAdditionalCategoryParameterAddition = useCallback(
    async (): Promise<boolean> =>
      runUndoAction({
        plan: lastAdditionalCategoryUndoPlan,
        projectUndoKey: currentProjectUndoKey,
        documentIdentity: expectedDocumentIdentity,
        onProgress: updateParameterProgressFromHost,
        didProjectChange,
        savePlan: setAdditionalCategoryParameterUndoPlan,
        setIsAdding,
        startProgress: startParameterProgress,
        notify: { showError, showInfo, showSuccess, showWarning },
        copy: AdditionalCategoryCopy,
      }),
    [
      currentProjectUndoKey,
      didProjectChange,
      expectedDocumentIdentity,
      lastAdditionalCategoryUndoPlan,
      setIsAdding,
      setAdditionalCategoryParameterUndoPlan,
      showError,
      showInfo,
      showSuccess,
      showWarning,
      startParameterProgress,
      updateParameterProgressFromHost,
    ],
  );

  return {
    undoLastConnectorParameterAddition,
    undoLastAdditionalCategoryParameterAddition,
  };
}
